import { API_BASE_URL } from "../../config";
import { useState } from "react";
import LoadingScreen from "./LoadingScreen";
import { startRecording, stopRecording } from "../../utils/audioRecorder";
import { speakText } from "../../utils/audioHelpers";
import "../../assets/css/pronunciation.css";

function MicIcon({ size }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" x2="12" y1="19" y2="22" />
        </svg>
    );
}

function wordClassName(word) {
    if (word.error_type === "Omission") {
        return "pronunciation-word pronunciation-word--omission";
    }
    if (word.accuracy_score >= 80) {
        return "pronunciation-word pronunciation-word--good";
    }
    if (word.accuracy_score >= 50) {
        return "pronunciation-word pronunciation-word--ok";
    }
    return "pronunciation-word pronunciation-word--poor";
}

function PronunciationPractice({ text, onResult = null, langCode = "de-DE" }) {
    const [recording, setRecording] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [recorderHandle, setRecorderHandle] = useState(null);

    async function handleMicClick() {
        if (loading) {
            return;
        }

        if (!recording) {
            try {
                setError(null);
                setResult(null);
                const handle = await startRecording();
                setRecorderHandle(handle);
                setRecording(true);
            } catch (err) {
                setError(err.message || "Microphone access denied.");
            }
            return;
        }

        setRecording(false);
        setLoading(true);

        try {
            const blob = await stopRecording(recorderHandle);
            setRecorderHandle(null);

            const formData = new FormData();
            formData.append("audio", blob, "recording.wav");
            formData.append("reference_text", text);
            formData.append("lang_code", langCode);

            const response = await fetch(`${API_BASE_URL}/api/pronunciation-assessment`, {
                method: "POST",
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Assessment failed.");
            }

            setResult(data);
            if (onResult) {
                onResult(data);
            }
        } catch (err) {
            setError(err.message || "Assessment failed.");
        } finally {
            setLoading(false);
        }
    }

    const micClass = recording
        ? "pronunciation-mic-btn pronunciation-mic-btn--recording"
        : "pronunciation-mic-btn";

    return (
        <div className="pronunciation-practice">
            <div className="pronunciation-prompt">{text}</div>
            <div className="pronunciation-controls">
                <button
                    type="button"
                    className={micClass}
                    aria-label={recording ? "Stop recording" : "Start recording"}
                    title={recording ? "Stop recording" : "Record pronunciation"}
                    onClick={handleMicClick}
                    disabled={loading}
                >
                    <MicIcon size={22} />
                </button>
            </div>

            {loading && (
                <div className="pronunciation-loading">
                    <LoadingScreen inline title={null} size={32} />
                </div>
            )}

            {error && <div className="pronunciation-error">{error}</div>}

            {result && !loading && (
                <>
                    <div className="pronunciation-overall-label">Overall score</div>
                    <div className="pronunciation-overall-score">
                        {Math.round(result.overall_score)}
                    </div>
                    <div className="pronunciation-words">
                        {result.words.map((word, index) => (
                            <div key={`${word.word}-${index}`} className="pronunciation-word-container">
                                <span 
                                    className={wordClassName(word)}
                                    onClick={() => speakText(word.word, langCode)}
                                    title="Click to hear pronunciation"
                                >
                                    {word.word}
                                </span>
                                {word.phonemes && word.phonemes.length > 0 && (
                                    <div className="pronunciation-phonemes-tooltip">
                                        {word.phonemes.map((p, i) => (
                                            <span key={i} className={wordClassName(p)}>
                                                {p.phoneme}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default PronunciationPractice;
